// src/hooks/useModalState.js
import { useState, useCallback } from 'react';

/**
 * Custom hook for managing multiple modal states
 * @param {Object} initialModals - Initial modal states
 * @returns {Object} Modal states and handlers
 */
export const useModalState = (initialModals = {}) => {
  const [modals, setModals] = useState({
    showDetailsModal: false,
    showEditPermissionModal: false,
    showBulkActionsModal: false,
    showAnalyticsModal: false,
    showNotificationsModal: false,
    ...initialModals
  });

  const [modalData, setModalData] = useState({
    selectedBiodataDetails: null,
    selectedBiodataForEdit: null,
    editPermissionData: {
      reason: '',
      expires_at: ''
    },
    bulkActionType: '',
    notifications: []
  });

  const [loadingStates, setLoadingStates] = useState({
    grantingPermission: false,
    bulkLoading: false
  });

  // Generic modal handler
  const openModal = useCallback((modalName, data = null) => {
    setModals(prev => ({ ...prev, [modalName]: true }));
    if (data) {
      setModalData(prev => ({ ...prev, ...data }));
    }
  }, []);

  const closeModal = useCallback((modalName, resetData = true) => {
    setModals(prev => ({ ...prev, [modalName]: false }));
    if (resetData) {
      // Reset specific modal data based on modal type
      setModalData(prev => {
        const newData = { ...prev };
        switch (modalName) {
          case 'showDetailsModal':
            newData.selectedBiodataDetails = null;
            break;
          case 'showEditPermissionModal':
            newData.selectedBiodataForEdit = null;
            newData.editPermissionData = { reason: '', expires_at: '' };
            break;
          case 'showBulkActionsModal':
            newData.bulkActionType = '';
            break;
          default:
            break;
        }
        return newData;
      });
    }
  }, []);

  const closeAllModals = useCallback(() => {
    setModals({
      showDetailsModal: false,
      showEditPermissionModal: false,
      showBulkActionsModal: false,
      showAnalyticsModal: false,
      showNotificationsModal: false
    });
    setModalData({
      selectedBiodataDetails: null,
      selectedBiodataForEdit: null,
      editPermissionData: { reason: '', expires_at: '' },
      bulkActionType: '',
      notifications: []
    });
  }, []);

  // Specific modal handlers
  const showDetailsModal = useCallback((biodataDetails) => {
    openModal('showDetailsModal', { selectedBiodataDetails: biodataDetails });
  }, [openModal]);

  const showEditPermissionModal = useCallback((biodata) => {
    openModal('showEditPermissionModal', { selectedBiodataForEdit: biodata });
  }, [openModal]);

  const showBulkActionsModal = useCallback((actionType) => {
    openModal('showBulkActionsModal', { bulkActionType: actionType });
  }, [openModal]);

  const showAnalyticsModal = useCallback(() => {
    openModal('showAnalyticsModal');
  }, [openModal]);

  const showNotificationsModal = useCallback((notifications = []) => {
    openModal('showNotificationsModal', { notifications });
  }, [openModal]);

  // Loading state handlers
  const setLoading = useCallback((loadingKey, isLoading) => {
    setLoadingStates(prev => ({ ...prev, [loadingKey]: isLoading }));
  }, []);

  // Update modal data
  const updateModalData = useCallback((updates) => {
    setModalData(prev => ({ ...prev, ...updates }));
  }, []);

  return {
    // Modal states
    modals,
    modalData,
    loadingStates,
    
    // Generic handlers
    openModal,
    closeModal,
    closeAllModals,
    
    // Specific handlers
    showDetailsModal,
    showEditPermissionModal,
    showBulkActionsModal,
    showAnalyticsModal,
    showNotificationsModal,
    
    // Loading handlers
    setLoading,
    
    // Data handlers
    updateModalData
  };
};